/**
 * EmployerPermissionsPage.jsx — static authority matrix help page.
 * Reflects the actual post-Patch-100 enforcement of the employer
 * workspace. Single source of truth in `lib/employerAuthority.js`.
 *
 * Read-only. No data fetch. No backend call. Pure FE static content.
 */
import { Link } from 'react-router-dom';
import { Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ShieldCheck, Check, X, User } from 'lucide-react';
import {
  PERMISSIONS_MATRIX, ROLE_LABEL, ROLE_DESCRIPTION,
} from '@/lib/employerAuthority';


function Cell({ value }) {
  if (value === 'yes') {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-200 border border-emerald-500/40">
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (value === 'self') {
    return (
      <Badge
        variant="outline"
        className="text-[10px] border-amber-300 text-amber-200 bg-amber-500/10"
        title="Reviewer can take this action only on their own review rows."
      >
        Own only
      </Badge>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-ndor-surface-alt text-slate-300 border border-slate-200">
      <X className="h-3.5 w-3.5" />
    </span>
  );
}


export default function EmployerPermissionsPage() {
  // Group matrix rows by `area`.
  const grouped = PERMISSIONS_MATRIX.reduce((acc, row) => {
    (acc[row.area] = acc[row.area] || []).push(row);
    return acc;
  }, {});

  return (
    <div
      className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6"
      data-testid="employer-permissions-page"
    >
      <header className="space-y-2">
        <Link
          to="/employer"
          className="inline-flex items-center text-xs text-ndor-ink-muted hover:text-ndor-ink"
          data-testid="employer-permissions-back"
        >
          <ArrowLeft className="h-3 w-3 mr-1" /> Back to Organisation
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight text-ndor-ink flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-ndor-ink-muted" />
          Roles &amp; permissions
        </h1>
        <p className="text-sm text-ndor-ink-secondary max-w-2xl">
          Reference of who can do what inside an organisation. Owners
          are admin-equivalent for every employer write. Recruiters
          run the day-to-day hiring and reviewer lifecycle. Reviewers
          decide on their own assigned reviews only. Every cell below
          mirrors the actual backend enforcement &mdash; if a button
          is missing for your role, it is governance, not a bug.
        </p>
      </header>

      {/* ── Role descriptions ────────────────────────────────── */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(ROLE_DESCRIPTION).map(([role, desc]) => (
          <Card
            key={role}
            data-testid={`employer-permissions-role-${role}`}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <User className="h-3.5 w-3.5 text-ndor-ink-muted" />
                {ROLE_LABEL[role]}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-ndor-ink-secondary leading-relaxed">
                {desc}
              </p>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* ── Matrix ───────────────────────────────────────────── */}
      <Card data-testid="employer-permissions-matrix">
        <CardHeader>
          <CardTitle className="text-sm font-medium">
            Permissions matrix
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-[10px] uppercase tracking-wider text-ndor-ink-muted">
                <th className="py-2 pr-3">Action</th>
                {['owner', 'admin', 'recruiter', 'reviewer'].map((r) => (
                  <th key={r} className="py-2 px-2 text-center">
                    {ROLE_LABEL[r]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([area, rows]) => (
                <Fragment key={area}>
                  <tr className="bg-ndor-surface-alt/40">
                    <td colSpan={5} className="py-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-ndor-ink-muted">
                      {area}
                    </td>
                  </tr>
                  {rows.map((row, i) => (
                    <tr
                      key={`${area}-${i}`}
                      className="border-b last:border-0"
                      data-testid={`employer-permissions-row-${row.action.replace(/\W+/g, '-').toLowerCase()}`}
                    >
                      <td className="py-2 pr-3 text-xs">{row.action}</td>
                      <td className="py-2 px-2 text-center"><Cell value={row.owner} /></td>
                      <td className="py-2 px-2 text-center"><Cell value={row.admin} /></td>
                      <td className="py-2 px-2 text-center"><Cell value={row.recruiter} /></td>
                      <td className="py-2 px-2 text-center"><Cell value={row.reviewer} /></td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-[11px] text-ndor-ink-muted">
        Append-only audit trail: every action above is recorded with
        actor, authority class, and timestamp in the account&apos;s
        governance ledger. Audit rows cannot be altered or deleted.
      </p>
    </div>
  );
}
